import { DOCUMENT } from '@angular/common';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { CanActivateFn, Router, Routes } from '@angular/router';

import { Auth } from 'src/providers/auth/auth';

import { Capacitor } from '@capacitor/core';
import { firstValueFrom, timeout } from 'rxjs';

// The pro website page always reads production pros, whichever environment
// links to it, so the check reads the same place.
const PRO_WEBSITE_API_URL = 'https://api-production.gotidy.com/api/v1/teams/';
const PRO_CHECK_TIMEOUT_MS = 5000;

// True for a one-segment path no page or redirect claims, which this guard may
// send to a pro's website.
export const isPossibleProWebsitePath = (path: string, config: Routes): boolean => {
  const segments = path.split('/').filter(Boolean);
  if (segments.length !== 1) {
    return false;
  }
  return !config.some((route) => !route.path?.startsWith(':') && route.path?.split('/')[0] === segments[0]);
};

// True for a pro's client hub link, /client/<id> or /<name>/client/<id>.
export const isClientHubPath = (path: string): boolean => /^\/([^/]+\/)?client\/[^/]+\/?$/.test(path);

export const proWebsiteRedirectGuard: CanActivateFn = async (route) => {
  const router = inject(Router);
  const document = inject(DOCUMENT);
  const auth = inject(Auth);
  const http = new HttpClient(inject(HttpBackend));
  const urlSuffix = route.paramMap.get('url_suffix');
  if (Capacitor.isNativePlatform() || !urlSuffix) {
    return router.parseUrl('/');
  }
  try {
    await firstValueFrom(http.get(PRO_WEBSITE_API_URL + encodeURIComponent(urlSuffix)).pipe(timeout(PRO_CHECK_TIMEOUT_MS)));
  } catch {
    // The app skipped its guest start-up for this path, so a reload gives a
    // signed-out visitor the dashboard with its usual sign-up popup.
    if (!auth.isAuthenticated()) {
      document.location.replace('/overview');
      return false;
    }
    return router.parseUrl('/');
  }
  document.location.replace(`/pro/${encodeURIComponent(urlSuffix)}`);
  return false;
};
